/**
 * One founder's record (admin-design-r1: records with many parts stay full pages): who they are in the header, then
 * starting a run, their runs, people, boxes and apps. Diagnose opens the box drawer in place (`?open=box:<slug>`).
 * Teardown asks for the slug before it starts (ConfirmAction, strength `type`). Coaching opens their conversations in
 * the Coaching section (`/coaching?founder=<id>`), which says so when their switch is off.
 */
import { useState } from 'react'
import { useParams } from 'react-router'
import { coachingLine } from '../../domain/coaching'
import { checkWords, cloudInitWords, seenWords, serverNow, versionView } from '../../domain/fleet'
import { activeBox, placementFor } from '../../domain/founder'
import { ago, elapsed, TEMPLATE_LABEL, when } from '../../domain/format'
import type { TemplateName } from '../../domain/schemas'
import { lightSignals, statusLook } from '../../domain/tones'
import { ApiError } from '../../infra/api'
import { Button, Card, Chip, ConfirmAction, DataTable, EmptyState, ErrorNote, Grid, Inline, LightStrip, Link, Screen, Stack, Text, useNav, useParam } from '../../ui'
import { useApi } from '../api-context'
import { AddPersonForm } from '../components/AddPersonForm'
import { PeopleTable } from '../components/People'
import { BoxDevices } from '../components/BoxDevices'
import { skillsWords } from '../../domain/devices'
import { StartRunDialog } from '../components/StartRunDialog'
import { UsageAdminCard } from '../components/UsageAdminCard'
import { useFleet } from '../useFleet'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'

/** The runs that open a form first; Teardown confirms in place. */
const RUN_BUTTONS: { template: Exclude<TemplateName, 'teardown'>; label: string }[] = [
  { template: 'full', label: 'Full' },
  { template: 'adopt', label: 'Adopt' },
  { template: 'boxOnly', label: 'Box only' },
  { template: 'addPerson', label: 'Add person' },
]

export function FounderPage() {
  const { id = '' } = useParams()
  const { api } = useApi()
  const { navigate } = useNav()
  const open = useParam('open')
  const d = useLoad(() => api.founder(id), [api, id])
  const [template, setTemplate] = useState<TemplateName | null>(null)
  const [tearing, setTearing] = useState(false)
  const [teardownError, setTeardownError] = useState<string>()
  // The agent's presence, version, lights and failing checks per box, polled like the Fleet page.
  const fleet = useFleet(id)
  const clock = useNow(1000)

  async function teardown() {
    setTearing(true)
    setTeardownError(undefined)
    try {
      const run = await api.createRun({ founderId: id, template: 'teardown', options: {} })
      navigate(`/runs/${run.id}`)
    } catch (e) {
      setTeardownError(e instanceof ApiError ? e.message : String(e))
      setTearing(false)
    }
  }

  const f = d.data?.founder
  return (
    <Screen
      title={f?.name ?? 'Founder'}
      crumb={{ label: 'Founders', to: '/founders' }}
      after={
        f && (
          <>
            <Chip tone={statusLook(f.status).tone} light={statusLook(f.status).light}>{f.status}</Chip>
            {d.data?.coaching?.on && <Chip tone="mute">{coachingLine(d.data.coaching, clock)}</Chip>}
          </>
        )
      }
      actions={
        f && (
          <Button size="sm" variant="ghost" icon="audit" onClick={() => navigate(`/coaching?founder=${encodeURIComponent(f.id)}`)}>
            Coaching
          </Button>
        )
      }
      description={f && [f.slug, f.region, f.boxSize, f.subdomain ? `${f.subdomain}.cpg.computer` : 'no domain', `since ${when(f.createdAt)}`].join(' · ')}
      query={d}
      skeleton="record"
    >
      {({ founder, boxes, apps, people, runs }) => {
        const placement = placementFor(runs)
        const box = activeBox({ boxes })
        const running = runs.find((r) => r.status === 'running')
        const agentNow = fleet.data ? serverNow(fleet.data.fleet.at, fleet.data.receivedAt, clock) : clock
        const agentRow = (boxId: string) => fleet.data?.fleet.boxes.find((r) => r.boxId === boxId)
        const within = fleet.data?.fleet.onlineWithinSec ?? 0
        const latest = fleet.data?.fleet.latestAgentVersion ?? null
        const busy = running ? 'A run is executing for this founder' : undefined

        return (
          <Stack>
            <Card title="Start a run">
              <Stack gap="sm">
                <Inline gap="sm">
                  {RUN_BUTTONS.map((b) => (
                    <Button key={b.template} size="sm" onClick={() => setTemplate(b.template)} disabled={Boolean(running)} title={busy}>
                      {b.label}
                    </Button>
                  ))}
                  <ConfirmAction
                    label="Teardown"
                    strength="type"
                    slug={founder.slug}
                    variant="ghost"
                    yes={`Tear down ${founder.slug}`}
                    question={`This deletes what cpg.computer created for ${founder.name}: the box, its DNS and the app, by tag and recorded ids. Attached repos and projects stay. It goes in the audit as you.`}
                    onConfirm={teardown}
                    disabled={Boolean(running)}
                    reason={busy}
                    working={tearing}
                  />
                </Inline>
                {running && (
                  <Text size="sm" tone="muted">
                    <Link to={`/runs/${running.id}`}>{`${TEMPLATE_LABEL[running.template]} run`}</Link> is executing; one run per founder at a time.
                  </Text>
                )}
                {teardownError && <ErrorNote title="Teardown didn't start." reason={teardownError} />}
              </Stack>
            </Card>

            <UsageAdminCard api={api} founderId={founder.id} now={clock} />

            <Grid cols={2}>
              <Card title="Runs" count={runs.length} flush>
                <DataTable
                  label="Runs"
                  framed={false}
                  rows={runs}
                  rowKey={(r) => r.id}
                  onOpen={(r) => navigate(`/runs/${r.id}`)}
                  attention={(r) => r.status === 'failed'}
                  empty={<EmptyState title="No runs yet.">Full sets up everything for a new founder.</EmptyState>}
                  columns={[
                    {
                      key: 'run',
                      label: 'Run',
                      narrow: 'main',
                      render: (r) => (
                        <Stack gap="none">
                          <Text weight="head">{TEMPLATE_LABEL[r.template]}</Text>
                          <Text mono size="xs" tone="muted">{`by ${r.createdBy}`}</Text>
                        </Stack>
                      ),
                    },
                    { key: 'status', label: 'Status', narrow: 'end', render: (r) => <Chip tone={statusLook(r.status).tone} light={statusLook(r.status).light}>{r.status}</Chip> },
                    { key: 'started', label: 'Started', render: (r) => <Text mono size="sm">{when(r.createdAt)}</Text> },
                    { key: 'took', label: 'Took', render: (r) => <Text mono size="sm" tone="muted">{elapsed(r.createdAt, r.finishedAt, Date.now(), r.status === 'running' ? null : r.updatedAt) ?? ''}</Text> },
                  ]}
                />
              </Card>

              <Card title="People" count={people.length} flush>
                <Stack gap="sm">
                  <PeopleTable people={people} reader="admin" />
                  <AddPersonForm founderId={founder.id} hasBox={Boolean(box)} busy={Boolean(running)} onAdded={(runId) => (runId ? navigate(`/runs/${runId}`) : d.reload())} />
                </Stack>
              </Card>

              <Card title="Boxes" count={boxes.length} flush>
                <DataTable
                  label="Boxes"
                  framed={false}
                  rows={boxes}
                  rowKey={(b) => b.id}
                  empty={<EmptyState title="No box yet.">Full or Box only creates one.</EmptyState>}
                  columns={[
                    { key: 'ip', label: 'Address', narrow: 'main', render: (b) => <Text mono weight="head">{b.ip ?? 'no IP yet'}</Text> },
                    { key: 'status', label: 'Status', narrow: 'end', render: (b) => <Chip tone={statusLook(b.status).tone} light={statusLook(b.status).light}>{b.status}</Chip> },
                    { key: 'where', label: 'Region · size', render: (b) => <Text mono size="sm">{`${b.region} · ${b.size}`}</Text> },
                    { key: 'droplet', label: 'Droplet', render: (b) => <Text mono size="sm">{b.dropletId ?? '–'}</Text> },
                    {
                      key: 'placement',
                      label: 'Placement',
                      render: (b) =>
                        b === box && placement ? (
                          <Text size="sm">
                            {placement.reason}
                            {placement.region && placement.region !== founder.region ? ` (asked ${founder.region})` : ''}
                          </Text>
                        ) : (
                          <Text size="sm" tone="muted">–</Text>
                        ),
                    },
                    {
                      key: 'heartbeat',
                      label: 'Heartbeat',
                      render: (b) => {
                        const r = agentRow(b.id)
                        if (!r || !fleet.data) return <Text size="sm" tone="muted">{b.lastHeartbeatAt ? ago(b.lastHeartbeatAt) : 'no agent yet'}</Text>
                        const s = seenWords(r, within, agentNow)
                        return <Chip tone={statusLook(s.presence).tone} light={statusLook(s.presence).light}>{s.words}</Chip>
                      },
                    },
                    {
                      key: 'agent',
                      label: 'Agent',
                      render: (b) => {
                        const r = agentRow(b.id)
                        if (!r) return <Text size="sm" tone="muted">{b.agentVersion ?? '–'}</Text>
                        const v = versionView(r, latest)
                        return (
                          <Inline gap="xs">
                            <Text mono size="sm">{v.words}</Text>
                            {v.behind ? <Chip tone="look" light="look">{v.note ?? 'behind'}</Chip> : v.note && <Text mono size="xs" tone="muted">{v.note}</Text>}
                          </Inline>
                        )
                      },
                    },
                    {
                      key: 'signals',
                      label: 'Signals',
                      render: (b) => {
                        const r = agentRow(b.id)
                        return r?.lights.length ? <LightStrip lights={lightSignals(r.lights)} /> : <Text size="sm" tone="muted">–</Text>
                      },
                    },
                    {
                      key: 'checks',
                      label: 'Checks',
                      render: (b) => {
                        const r = agentRow(b.id)
                        if (!r) return <Text size="sm" tone="muted">–</Text>
                        const ci = cloudInitWords(r.cloudInit)
                        if (!r.failingChecks.length && !ci) return <Text size="sm" tone="muted">{r.health ? 'All passing' : 'Nothing reported yet'}</Text>
                        return (
                          <Stack gap="xs">
                            {r.failingChecks.map((c) => (
                              <Text key={c.key} size="sm" weight="head">{checkWords(c).title}</Text>
                            ))}
                            {ci && <Text size="sm" tone={ci.bad ? 'default' : 'muted'}>{ci.words}</Text>}
                          </Stack>
                        )
                      },
                    },
                    {
                      key: 'skills',
                      label: 'Base skills',
                      render: (b) => {
                        const w = skillsWords(agentRow(b.id)?.skills, clock)
                        if (!w.commit) return <Text size="sm" tone="muted">–</Text>
                        return (
                          <Stack gap="xs">
                            <Text mono size="sm">{w.commit}</Text>
                            {w.restored && <Chip tone="look" light="look">{w.restored}</Chip>}
                          </Stack>
                        )
                      },
                    },
                    {
                      key: 'diagnose',
                      label: 'Diagnosis',
                      narrowLabel: false,
                      render: (b) =>
                        b.status !== 'destroyed' && agentRow(b.id) ? (
                          <Button size="xs" onClick={() => open.set(`box:${founder.slug}`)}>
                            Diagnose
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              </Card>

              {box && (
                <Card title="Paired devices" flush>
                  <BoxDevices api={api} founderId={founder.id} founderUser={people.find((p) => p.role === 'founder')?.linuxUser ?? null} people={people} onDone={() => d.reload()} />
                </Card>
              )}

              <Card title="Apps" count={apps.length} flush>
                <DataTable
                  label="Apps"
                  framed={false}
                  rows={apps}
                  rowKey={(a) => a.id}
                  empty={<EmptyState title="No app yet.">Full creates one from the starter; Adopt attaches one.</EmptyState>}
                  columns={[
                    { key: 'repo', label: 'Repo', narrow: 'main', render: (a) => <Text mono weight="head">{a.repo ?? 'no repo'}</Text> },
                    { key: 'origin', label: 'Origin', narrow: 'end', render: (a) => <Chip tone="mute">{a.origin}</Chip> },
                    { key: 'domain', label: 'Domain', render: (a) => (a.customDomain || a.domain ? <Text mono size="sm">{a.customDomain ?? a.domain}</Text> : <Text size="sm" tone="muted">none</Text>) },
                    { key: 'deploy', label: 'Deploy', render: (a) => <Text mono size="sm">{a.railwayWorkspace ? `railway · ${a.railwayWorkspace}` : '–'}</Text> },
                    { key: 'project', label: 'Project', render: (a) => <Text mono size="sm">{a.railwayProjectId ?? '–'}</Text> },
                    { key: 'service', label: 'Service', render: (a) => <Text mono size="sm">{a.railwayServiceId ?? '–'}</Text> },
                    { key: 'installation', label: 'Installation', render: (a) => <Text mono size="sm">{a.installationId ?? '–'}</Text> },
                  ]}
                />
              </Card>
            </Grid>

            <StartRunDialog founder={founder} template={template} onClose={() => setTemplate(null)} onStarted={(runId) => navigate(`/runs/${runId}`)} />
          </Stack>
        )
      }}
    </Screen>
  )
}
